import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getCampaigns, deleteCampaign } from "../../services/campaignService";
import { toast } from "react-toastify";
import { FaEye, FaEdit, FaTrash } from "react-icons/fa";

const CampaignLayerList = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  /* Pagination */
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const res = await getCampaigns();
    if (res.status === "success") {
      setCampaigns(res.data || []);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this campaign?")) return;

    const res = await deleteCampaign(id);
    if (res.status === "success") {
      toast.success("Campaign deleted");
      loadData();
    } else {
      toast.error(res.message || "Delete failed");
    }
  };

  /* ================= FILTERED DATA ================= */
  const filteredCampaigns = useMemo(() => {
    return campaigns.filter((c) => {
      const matchSearch = c.name
        ?.toLowerCase()
        .includes(search.toLowerCase());

      const matchStatus =
        statusFilter === "all" || c.status === statusFilter;

      return matchSearch && matchStatus;
    });
  }, [campaigns, search, statusFilter]);

  /* ================= PAGINATION ================= */
  const totalPages = Math.ceil(filteredCampaigns.length / pageSize);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredCampaigns.slice(start, start + pageSize);
  }, [filteredCampaigns, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, pageSize]);

  return (
    <div className="add-user-card">
      {/* HEADER */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5>Campaigns</h5>
        <button
          className="btn btn-primary"
          onClick={() => navigate("/add-campaign")}
        >
          + Add Campaign
        </button>
      </div>

      {/* FILTERS */}
      <div className="row mb-3">
        <div className="col-md-4">
          <input
            type="text"
            className="form-control"
            placeholder="Search campaign..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="col-md-3">
          <select
            className="form-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        <div className="col-md-2">
          <select
            className="form-select"
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
          >
            <option value={5}>5 / page</option>
            <option value={10}>10 / page</option>
            <option value={20}>20 / page</option>
          </select>
        </div>
      </div>

      {/* TABLE */}
      <table className="table table-bordered align-middle">
        <thead>
          <tr>
            <th>#</th>
            <th>Campaign Name</th>
            <th>Status</th>
            <th>Duration</th>
            <th width="160">Action</th>
          </tr>
        </thead>
        <tbody>
          {paginatedData.length === 0 ? (
            <tr>
              <td colSpan="5" className="text-center text-muted">
                No campaigns found
              </td>
            </tr>
          ) : (
            paginatedData.map((c, i) => (
              <tr key={c.id}>
                <td>{(currentPage - 1) * pageSize + i + 1}</td>
                <td>{c.name}</td>
                <td>
                  <span
                    className={`badge ${
                      c.status === "active"
                        ? "bg-success"
                        : "bg-secondary"
                    }`}
                  >
                    {c.status}
                  </span>
                </td>
                <td>
                  {c.start_date} → {c.end_date}
                </td>
                <td>
                  <button
                    className="btn btn-sm btn-outline-primary me-1"
                    onClick={() => navigate(`/view-campaign/${c.id}`)}
                  >
                    <FaEye />
                  </button>

                  <button
                    className="btn btn-sm btn-outline-info me-1"
                    onClick={() => navigate(`/add-campaign/${c.id}`)}
                  >
                    <FaEdit />
                  </button>

                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => handleDelete(c.id)}
                  >
                    <FaTrash />
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {/* PAGINATION */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <span className="text-muted">
            Page {currentPage} of {totalPages}
          </span>

          <div>
            <button
              className="btn btn-sm btn-outline-secondary me-2"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              Prev
            </button>

            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i}
                className={`btn btn-sm me-1 ${
                  currentPage === i + 1
                    ? "btn-primary"
                    : "btn-outline-secondary"
                }`}
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}

            <button
              className="btn btn-sm btn-outline-secondary"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignLayerList;
